import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import AHDateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import { z } from "zod";

export type BookmarkJSON = z.infer<typeof BookmarkSchema.BookmarkJSONZ>;
export type BookmarkParams = z.infer<typeof BookmarkSchema.BookmarkZ>;

export default class BookmarkSchema {
  public static readonly BookmarkJSONZ = z.object({
    id: z.uuid(),
    createdDate: z.iso.datetime({ offset: true }),
  });
  public static readonly BookmarkZ = z.object({
    id: z.instanceof(Guid),
    createdDate: AHDateSchema.type,
  });

  public static validate(values: unknown): BookmarkParams {
    return parseOrThrow(BookmarkSchema.BookmarkZ, values, {
      logError: true,
      schemaName: "BookmarkSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): BookmarkJSON {
    return parseOrThrow(BookmarkSchema.BookmarkJSONZ, values, {
      logError: true,
      schemaName: "BookmarkSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is BookmarkParams {
    try {
      BookmarkSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is BookmarkJSON {
    try {
      BookmarkSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: BookmarkJSON): BookmarkParams {
    const parsed = parseOrThrow(BookmarkSchema.BookmarkJSONZ, data, {
      logError: true,
      schemaName: "BookmarkSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return BookmarkSchema.codec.decode(parsed);
  }

  public static serialize(document: BookmarkParams): BookmarkJSON {
    const parsed = BookmarkSchema.validate(document);
    return BookmarkSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(BookmarkSchema.BookmarkJSONZ, BookmarkSchema.BookmarkZ, {
    decode: (data) => ({
      id: new Guid(GuidSchema.deserialize(data.id)),
      createdDate: new AHDate(AHDateSchema.deserialize(data.createdDate)),
    }),
    encode: (bookmark) => ({
      id: bookmark.id.toJSON(),
      createdDate: bookmark.createdDate.toJSON(),
    }),
  });
}
